import React, { useEffect, useRef, useState } from 'react';
import { DraftCostData, DraftCostRow, getDraftCostBreakdown } from '../api/emailActivity';

function csvCell(value: unknown): string {
  let text = value == null ? '' : String(value);
  if (typeof value !== 'number' && (/^[\s]*[=+@-]/.test(text) || /^[\t\r\n]/.test(text))) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildDraftCsv(data: DraftCostData): string {
  const rows = data.rows.filter(row => row.included);
  const difference = data.recordedUnits == null ? null : rows.length - data.recordedUnits;
  const header = ['Claim reference', 'Generated at', 'Archive ID', 'Message ID', 'Source claim', 'Scope', 'Date', 'Description', 'Recipient', 'Status', 'Units', 'Rate', 'Recorded units', 'Draft units', 'Difference'];
  const records: unknown[][] = rows.map(row => [data.claimId, data.generatedAt, row.id, row.messageId, row.claimId, row.scope, row.sentAt, row.description, row.recipient, row.status, 1, row.rate, data.recordedUnits, rows.length, difference]);
  if (!rows.length) records.push([data.claimId, data.generatedAt, '', '', '', '', '', 'No messages selected', '', '', 0, '', data.recordedUnits, 0, difference]);
  return [header, ...records].map(row => row.map(csvCell).join(',')).join('\r\n');
}

const fieldStyle: React.CSSProperties = { border: '1px solid #aaa', borderRadius: 4, padding: 6, maxWidth: '100%' };
export default function DraftCostBreakdown() {
  const [claim, setClaim] = useState('');
  const [data, setData] = useState<DraftCostData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(0);
  useEffect(() => () => { request.current += 1; }, []);
  const clear = () => { request.current += 1; setData(null); setError(''); setLoading(false); };
  const load = async (event: React.FormEvent) => {
    event.preventDefault();
    const reference = claim.trim();
    clear();
    if (!reference) { setError('Enter a claim reference.'); return; }
    const current = request.current;
    setLoading(true);
    try {
      const result = await getDraftCostBreakdown(reference);
      if (request.current === current) setData({ ...result, rows: result.rows.map(row => ({ ...row, rate: '' })) });
    } catch (err: any) {
      if (request.current === current) setError(err?.response?.data?.error || err?.message || 'Failed to load draft.');
    } finally { if (request.current === current) setLoading(false); }
  };
  const edit = (index: number, patch: Partial<DraftCostRow>) => setData(current => current && ({ ...current, rows: current.rows.map((row, i) => i === index ? { ...row, ...patch } : row) }));
  const units = data?.rows.filter(row => row.included).length || 0;
  const exportCsv = () => {
    if (!data) return;
    const url = URL.createObjectURL(new Blob([buildDraftCsv(data)], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url; link.download = `draft-costs-${data.claimId.replace(/[^a-zA-Z0-9._-]/g, '_')}.csv`;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  return <details style={{ background: 'white', border: '1px solid #ddd', padding: 16, borderRadius: 8, marginBottom: 20 }}>
    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Draft costs breakdown</summary>
    <p>Review MailerSend archive messages as one unit each. Archive coverage is not a complete ledger. This draft does not bill, send, or save edits.</p>
    <form onSubmit={load} style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap' }}>
      <label>Draft claim reference<br /><input style={fieldStyle} value={claim} onChange={event => { clear(); setClaim(event.target.value); }} /></label>
      <button type="submit" disabled={loading} style={fieldStyle}>{loading ? 'Loading…' : 'Load draft'}</button>
    </form>
    {error && <p role="alert">{error}</p>}
    {data && <>
      <h2 style={{ fontSize: 18 }}>Draft for {data.claimId}</h2>
      <p>Shared email matches may relate to other claims: review every included message. Messages explicitly linked to another claim start excluded. Enter reviewer rates only if required.</p>
      {!!data.excludedAddresses && <p>Emails to {data.excludedAddresses === 1 ? 'one previous email address are' : `${data.excludedAddresses} previous email addresses are`} not included, because another client uses {data.excludedAddresses === 1 ? 'it' : 'them'} now.</p>}
      <div aria-live="polite" style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <p>Draft units: {units}</p><p>Recorded units: {data.recordedUnits ?? 'Not available'}</p>
        <p>Difference: {data.recordedUnits == null ? 'Not available' : units - data.recordedUnits}</p>
      </div>
      <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', textAlign: 'left', borderSpacing: 8 }}>
        <caption>Editable draft messages; recorded units remain unchanged</caption>
        <thead><tr>{['Include', 'Date', 'Description', 'Recipient / source', 'Status', 'Units', 'Rate'].map(heading => <th key={heading} scope="col">{heading}</th>)}</tr></thead>
        <tbody>{data.rows.map((row, index) => <tr key={row.id}>
          <td><input type="checkbox" aria-label={`Include message ${index + 1}`} checked={row.included} onChange={event => edit(index, { included: event.target.checked })} /></td>
          <td><input style={fieldStyle} aria-label={`Date for message ${index + 1}`} value={row.sentAt || ''} onChange={event => edit(index, { sentAt: event.target.value })} /></td>
          <td><input style={fieldStyle} aria-label={`Description for message ${index + 1}`} value={row.description} onChange={event => edit(index, { description: event.target.value })} /></td>
          <td>{row.recipient}<br /><small>{row.scope === 'client_email' ? 'Shared email match' : row.claimId} · {row.messageId || row.id}</small>{row.sentToPreviousEmail && <><br /><small>Sent to a previous email address</small></>}</td>
          <td>{row.status || 'Unknown'}</td><td>1</td>
          <td><input style={{ ...fieldStyle, width: 90 }} type="number" min="0" step="0.01" aria-label={`Rate for message ${index + 1}`} value={row.rate} onChange={event => edit(index, { rate: event.target.value })} /></td>
        </tr>)}</tbody>
      </table></div>
      {!data.rows.length && <p>No archived messages found for this claim.</p>}
      <details style={{ margin: '16px 0' }}>
        <summary>Recorded unit history (read-only)</summary>
        {data.recordedHistory.length ? <div style={{ overflowX: 'auto' }}><table style={{ textAlign: 'left', borderSpacing: 8 }}>
          <thead><tr>{['Date', 'Reason', 'Delta', 'Total', 'Source'].map(label => <th scope="col" key={label}>{label}</th>)}</tr></thead>
          <tbody>{data.recordedHistory.map((entry, index) => <tr key={index}><td>{entry.timestamp || '—'}</td><td>{entry.reason || '—'}</td><td>{entry.delta ?? '—'}</td><td>{entry.total ?? '—'}</td><td>{entry.source || '—'}</td></tr>)}</tbody>
        </table></div> : <p>No recorded unit history available.</p>}
      </details>
      <button type="button" style={fieldStyle} onClick={exportCsv}>Export CSV</button>
    </>}
  </details>;
}
